import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from "react-router-dom";
import UserInfoDropdown from './UserInfoDropdown';
import localStorageService from '../services/localStorageService';
import { AuthContext } from '../store/ContextStore';
import { SUBSCRIPTION, TRIAL_EXPIRED, TEAM_SEAT_OVERFLOW } from '../constants/routes';

const SPORT_METADATA: Record<string, { label: string; image: string }> = {
  MFB: { label: "Football", image: "football.png" },
  MBB: { label: "Basketball", image: "basketball.png" },
  WBB: { label: "Basketball", image: "basketball.png" },
  MIH: { label: "Ice Hockey", image: "icehockey.png" },
  WIH: { label: "Ice Hockey", image: "icehockey.png" },
  MSO: { label: "Soccer", image: "soccer.png" },
  WSO: { label: "Soccer", image: "soccer.png" },
  MVB: { label: "Volleyball", image: "volleyball.png" },
  WVB: { label: "Volleyball", image: "volleyball.png" },
};

const SPORT_GROUPS = [
  {
    key: "football",
    displayName: "Football",
    variants: [
      { code: "MFB", badge: "M" },
    ],
  },
  {
    key: "basketball",
    displayName: "Basketball",
    variants: [
      { code: "MBB", badge: "M" },
      { code: "WBB", badge: "W" },
    ],
  },
  {
    key: "icehockey",
    displayName: "Ice Hockey",
    variants: [
      { code: "MIH", badge: "M" },
      { code: "WIH", badge: "W" },
    ],
  },
  {
    key: "soccer",
    displayName: "Soccer",
    variants: [
      { code: "MSO", badge: "M" },
      { code: "WSO", badge: "W" },
    ],
  },
  {
    key: "Volleyball",
    displayName: "Volleyball",
    variants: [
      { code: "MVB", badge: "M" },
      { code: "WVB", badge: "W" },
    ],
  }
];

const SPORT_VARIANTS = SPORT_GROUPS.flatMap((group) =>
  group.variants.map((variant) => ({
    ...variant,
    ...SPORT_METADATA[variant.code],
    groupName: group.displayName,
  }))
);

const MAX_HEADER_SPORTS = 4;
const DESKTOP_SPORT_CARD_WIDTH = 120;
const DESKTOP_SPORT_GAP = 12;

function Header({ trialExpired }: { trialExpired?: boolean }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedSport, setSelectedSport, user, team } = useContext(AuthContext);

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const userDropdownRef = useRef<HTMLDivElement | null>(null);
  const sportRailRef = useRef<HTMLDivElement | null>(null);
  const scrollPersistRafRef = useRef<number | null>(null);
  void trialExpired;

  const isOnTrialExpiredPage = location.pathname === TRIAL_EXPIRED;
  const teamLogo = team?.imgUrl || "";
  const teamName = team?.name || "";
  const profilePic = "/user_profile_pic.png";
  const userName = user?.username || "";
  const accentColor = team?.colorCode ?? '#0ea5e9';
  const sportRailScrollKey = `sportRailScrollLeft:${user?.username || 'anon'}`;

  const userSportCodes: string[] = user?.sports?.map((sport: any) => sport.code) ?? [];
  const visibleVariants = SPORT_VARIANTS.filter((variant) => userSportCodes.includes(variant.code));
  const showDesktopArrows = visibleVariants.length > MAX_HEADER_SPORTS;
  const desktopRailWidth =
    MAX_HEADER_SPORTS * DESKTOP_SPORT_CARD_WIDTH + (MAX_HEADER_SPORTS - 1) * DESKTOP_SPORT_GAP;
  const nowSec = Math.floor(Date.now() / 1000);

  const sportAccess: Record<string, any> =
    (user as any)?.subscription?.sport_access ||
    (user as any)?.subscription?.sportAccess ||
    {};

  function isVariantLockedOrExpired(variantCode: string): boolean {
    const row = sportAccess?.[variantCode];
    const status = String(row?.status || '').toLowerCase();
    const untilSec = Number(row?.until);

    if (status === 'locked') return true;
    if (status === 'trial_end') return true;
    if (status === 'trial' && Number.isFinite(untilSec) && untilSec > 0 && untilSec <= nowSec) return true;
    return false;
  }

  function handleSportSelect(code: string) {
    const rail = sportRailRef.current;
    if (rail) {
      try {
        sessionStorage.setItem(sportRailScrollKey, String(rail.scrollLeft));
      } catch {
        // ignore
      }
    }

    // Check if team seat limit is reached - if so, navigate to overflow page.
    // We check BOTH the global limit_reached flag (from login response) AND the
    // per-sport seat_locked status (from subscription sport_access). The per-sport
    // check is the same one used by App.tsx and TeamSeatOverflow.tsx, ensuring
    // consistency. Without this, clicking the same sport twice would bypass the
    // overflow because App.tsx's useEffect wouldn't re-fire for an unchanged sport.
    const userInfo = localStorageService.getUserInfo();
    const teamSeatInfo = (userInfo as any)?.team_seat_info || (user as any)?.team_seat_info;
    const isSeatLocked = sportAccess?.[code]?.status === 'seat_locked';
    if (teamSeatInfo?.limit_reached || isSeatLocked) {
      setSelectedSport(code);
      localStorageService.setSelectedSport(code);
      navigate(TEAM_SEAT_OVERFLOW, { replace: true });
      return;
    }

    setSelectedSport(code);
    localStorageService.setSelectedSport(code);
    localStorageService.addNLPFilter(null);
    setIsMobileMenuOpen(false);
    navigate("/dashboard");
  }

  function syncSportScrollState(rail: HTMLDivElement, nextScrollLeft: number = rail.scrollLeft) {
    const maxScrollLeft = Math.max(0, rail.scrollWidth - rail.clientWidth);
    const clampedScrollLeft = Math.min(Math.max(nextScrollLeft, 0), maxScrollLeft);

    setCanScrollLeft(clampedScrollLeft > 4);
    setCanScrollRight(maxScrollLeft - clampedScrollLeft > 4);
  }

  function updateSportScrollState() {
    const rail = sportRailRef.current;
    if (!rail || !showDesktopArrows) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }

    syncSportScrollState(rail);
  }

  function scrollSportRail(direction: 'left' | 'right') {
    const rail = sportRailRef.current;
    if (!rail) return;

    const firstCard = rail.querySelector('button');
    const cardWidth = firstCard?.getBoundingClientRect().width ?? DESKTOP_SPORT_CARD_WIDTH;
    const scrollAmount = cardWidth + DESKTOP_SPORT_GAP;
    const nextScrollLeft = rail.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount);

    syncSportScrollState(rail, nextScrollLeft);

    rail.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });

    window.requestAnimationFrame(() => {
      updateSportScrollState();
    });
  }

  useEffect(() => {
    updateSportScrollState();

    const rail = sportRailRef.current;
    if (!rail) return;

    const handleScroll = () => {
      updateSportScrollState();

      if (scrollPersistRafRef.current !== null) {
        window.cancelAnimationFrame(scrollPersistRafRef.current);
      }

      scrollPersistRafRef.current = window.requestAnimationFrame(() => {
        scrollPersistRafRef.current = null;
        try {
          sessionStorage.setItem(sportRailScrollKey, String(rail.scrollLeft));
        } catch {
          // ignore
        }
      });
    };
    rail.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);

    return () => {
      rail.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
      if (scrollPersistRafRef.current !== null) {
        window.cancelAnimationFrame(scrollPersistRafRef.current);
        scrollPersistRafRef.current = null;
      }
    };
  }, [showDesktopArrows, visibleVariants.length, sportRailScrollKey]);

  useEffect(() => {
    window.requestAnimationFrame(() => {
      updateSportScrollState();
    });
  }, [selectedSport, visibleVariants.length]);

  useEffect(() => {
    const rail = sportRailRef.current;
    if (!rail) return;

    // Restore previous scroll position so selecting a sport doesn't jump the rail back to the start.
    try {
      const raw = sessionStorage.getItem(sportRailScrollKey);
      const saved = raw === null ? null : Number(raw);
      if (saved !== null && Number.isFinite(saved)) {
        rail.scrollLeft = saved;
      }
    } catch {
      // ignore
    }

    // Ensure the active sport remains visible after navigation/remount.
    window.requestAnimationFrame(() => {
      const activeButton = rail.querySelector<HTMLButtonElement>(`button[data-sport-code="${selectedSport}"]`);
      if (!activeButton) return;

      const railRect = rail.getBoundingClientRect();
      const btnRect = activeButton.getBoundingClientRect();
      const isFullyVisible = btnRect.left >= railRect.left && btnRect.right <= railRect.right;
      if (isFullyVisible) return;

      activeButton.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    });
  }, [selectedSport, sportRailScrollKey]);

  useEffect(() => {
    if (!isUserDropdownOpen) return;

    function handleOutsideClick(event: MouseEvent | TouchEvent) {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target as Node)) {
        setIsUserDropdownOpen(false);
      }
    }

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, [isUserDropdownOpen]);

  const selectedSportAccessRow = sportAccess?.[selectedSport];
  const selectedSportStatus = String(selectedSportAccessRow?.status || '').toLowerCase();
  const selectedSportUntilSec = Number(selectedSportAccessRow?.until);

  const isTrial = selectedSportStatus === 'trial';
  const isTrialExpired =
    selectedSportStatus === 'trial_end' ||
    (isTrial && Number.isFinite(selectedSportUntilSec) && selectedSportUntilSec > 0 && selectedSportUntilSec <= nowSec);
  const isTrialActive = isTrial && Number.isFinite(selectedSportUntilSec) && selectedSportUntilSec > nowSec;
  const isLocked = selectedSportStatus === 'locked';

  const trialDaysLeftForSelectedSport =
    isTrialActive && Number.isFinite(selectedSportUntilSec)
      ? Math.max(0, Math.ceil((selectedSportUntilSec - nowSec) / (60 * 60 * 24)))
      : null;

  return (
    <header className="bg-white min-h-[78px] md:min-h-[112px] px-4 py-3 md:py-4 flex items-center gap-4 md:grid md:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] md:items-center relative shadow-sm overflow-visible">
      <div className="flex flex-row items-center gap-3 flex-shrink-0 md:w-full md:min-w-0 md:justify-self-start">
        <Link to="/dashboard" className="flex items-center gap-1">
          <img src="/athlyte_logo.png" alt="Athlyte logo" className="w-[42px]" />
          <span className="text-gray-300 font-bold text-lg">|</span>
        </Link>
        <Link to="/dashboard" className="flex items-center gap-2">
          <img src={teamLogo} alt="Team logo" className="w-[42px]" />
          <p className="text-sm font-bold text-gray-800 font-secondary hidden sm:block">
            {teamName}
          </p>
        </Link>
      </div>

      <nav className="hidden md:flex min-w-0 justify-self-center">
        <div className="flex w-full max-w-[640px] items-center gap-3">
          {showDesktopArrows && (
            <button
              type="button"
              onClick={() => scrollSportRail('left')}
              disabled={!canScrollLeft}
              aria-label="Scroll sports left"
              className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full border transition-all ${
                canScrollLeft
                  ? 'shadow-sm hover:brightness-110'
                  : 'border-slate-200 bg-slate-100 text-slate-300 cursor-not-allowed'
              }`}
              style={canScrollLeft ? { borderColor: accentColor, backgroundColor: accentColor, color: '#ffffff' } : undefined}
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 18l-6-6 6-6" />
              </svg>
            </button>
          )}

          <div
            ref={sportRailRef}
            className={`flex items-stretch gap-3 overflow-x-auto scroll-smooth snap-x snap-mandatory py-1 ${
              showDesktopArrows ? '' : 'justify-center'
            }`}
            style={{
              scrollbarWidth: 'none',
              width: showDesktopArrows ? `min(100%, ${desktopRailWidth}px)` : '100%',
              maxWidth: `${desktopRailWidth}px`,
            }}
          >
            {visibleVariants.map((variant) => {
              const isActive = selectedSport === variant.code;
              const isLockedOrExpired = isVariantLockedOrExpired(variant.code);

              return (
                <button
                  key={variant.code}
                  type="button"
                  onClick={() => handleSportSelect(variant.code)}
                  data-sport-code={variant.code}
                  className={`relative w-[120px] min-w-[120px] flex-shrink-0 snap-start rounded-[22px] border px-1 py-2 text-center transition-all duration-200 ${
                    isActive
                      ? 'border-2 shadow-[0_12px_28px_-20px_rgba(14,165,233,0.85)]'
                      : 'border-slate-200 shadow-[0_12px_24px_-22px_rgba(15,23,42,0.65)] hover:-translate-y-0.5 hover:border-slate-300'
                  }`}
                  style={isActive ? { borderColor: accentColor, backgroundColor: '#f7fcff' } : { backgroundColor: '#ffffff' }}
                >
                  <span
                    className={`absolute right-3 top-3 rounded-full px-2 py-0.5 text-[10px] font-bold ${
                      variant.badge === 'W' ? 'bg-rose-100 text-rose-700' : 'bg-sky-100 text-sky-700'
                    }`}
                  >
                    {variant.badge}
                  </span>

                  <img
                    src={`/${SPORT_METADATA[variant.code].image}`}
                    alt={SPORT_METADATA[variant.code].label}
                    className={`mx-auto h-[24px] w-[24px] object-contain ${isLockedOrExpired ? 'grayscale opacity-60' : ''}`}
                  />

                  <span
                    className="mt-2 block text-sm font-semibold leading-tight"
                    style={isActive ? { color: accentColor } : { color: '#0f172a' }}
                  >
                    {SPORT_METADATA[variant.code].label}
                  </span>
                </button>
              );
            })}
          </div>

          {showDesktopArrows && (
            <button
              type="button"
              onClick={() => scrollSportRail('right')}
              disabled={!canScrollRight}
              aria-label="Scroll sports right"
              className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full border transition-all ${
                canScrollRight
                  ? 'shadow-sm hover:brightness-110'
                  : 'border-slate-200 bg-slate-100 text-slate-300 cursor-not-allowed'
              }`}
              style={canScrollRight ? { borderColor: accentColor, backgroundColor: accentColor, color: '#ffffff' } : undefined}
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 6l6 6-6 6" />
              </svg>
            </button>
          )}
        </div>
      </nav>

      <div className="flex flex-row items-center gap-3 flex-shrink-0 md:w-full md:min-w-0 md:justify-end md:justify-self-end">
        {!isOnTrialExpiredPage && (
          isTrialExpired ? (
            <div className="hidden xl:flex flex-row items-center gap-3 px-3 py-2 bg-white rounded-md">
              <div className="flex flex-col gap-0.5">
                <span className="text-xs text-gray-900 font-medium">Trial has expired</span>
              </div>
              <button
                onClick={() => navigate(SUBSCRIPTION)}
                className="px-3 py-1 bg-gradient-to-r from-[#D84315] to-[#FFB74D] hover:brightness-110 text-white text-xs font-bold rounded-full transition-all whitespace-nowrap flex-shrink-0 flex items-center gap-1 shadow-sm"
              >
                Upgrade <span>⚡</span>
              </button>
            </div>
          ) : isTrialActive && trialDaysLeftForSelectedSport !== null ? (
            <div className="hidden xl:flex flex-row items-center gap-2 px-3 py-2 bg-white rounded-md whitespace-nowrap flex-shrink-0">
              <span className="text-xs text-gray-900 font-medium">
                Your trial ends in {trialDaysLeftForSelectedSport} days
              </span>
              <button
                onClick={() => navigate(SUBSCRIPTION)}
                className="px-2 py-1 bg-orange-500 hover:bg-orange-600 text-white text-xs font-bold rounded-full transition-colors flex items-center gap-1"
              >
                Upgrade ⚡
              </button>
            </div>
          ) : isLocked ? (
            <div className="hidden xl:flex flex-row items-center gap-3 px-3 py-2 bg-white rounded-md">
              <button
                onClick={() => navigate(SUBSCRIPTION)}
                className="px-3 py-1 bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold rounded-full transition-colors whitespace-nowrap flex-shrink-0 flex items-center gap-1"
              >
                Subscribe
              </button>
            </div>
          ) : null
        )}

        <div
          ref={userDropdownRef}
          className="relative flex items-center gap-2 cursor-pointer"
          onClick={() => setIsUserDropdownOpen((prev) => !prev)}
        >
          <img className="w-[38px] sm:w-[48px] rounded-full" src={profilePic} alt="profile" />
          <p className="hidden sm:block font-primary text-base font-semibold">{userName}</p>

          <svg
            className={`w-4 h-4 ml-1 transition-transform duration-200 ${isUserDropdownOpen ? 'rotate-180' : ''}`}
            aria-hidden="true"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M19 9l-7 7-7-7"
            />
          </svg>

          <UserInfoDropdown
            isOpen={isUserDropdownOpen}
            setIsOpen={setIsUserDropdownOpen}
          />
        </div>

        <button
          className="flex md:hidden p-2 focus:outline-none"
          onClick={() => setIsMobileMenuOpen((prev) => !prev)}
          aria-label="Toggle sport menu"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {isMobileMenuOpen
              ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            }
          </svg>
        </button>
      </div>

      {isMobileMenuOpen && (
        <div className="md:hidden absolute top-full left-0 right-0 bg-white border-t border-gray-100 shadow-md z-50 px-4 py-3">
          <div className="flex flex-col gap-3">
            {SPORT_GROUPS.map((group) => {
              const groupVariants = group.variants.filter((variant) => userSportCodes.includes(variant.code));
              if (groupVariants.length === 0) return null;

              return (
                <div key={group.key}>
                  <p className="text-xs text-gray-400 uppercase font-semibold px-1 pb-2">
                    {group.displayName}
                  </p>
                  <div className="flex flex-col gap-2">
                    {groupVariants.map((variant) => (
                      <button
                        key={variant.code}
                        type="button"
                        className={`w-full flex items-center gap-3 rounded-2xl border px-3 py-3 text-left transition-colors ${
                          selectedSport === variant.code ? 'font-bold border-2' : 'border-slate-200'
                        }`}
                        style={selectedSport === variant.code ? { borderColor: accentColor, backgroundColor: '#f7fcff' } : {}}
                        onClick={() => handleSportSelect(variant.code)}
                      >
                        <img src={`/${SPORT_METADATA[variant.code].image}`} alt={SPORT_METADATA[variant.code].label} className="h-10 w-10 object-contain" />
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-slate-900">{SPORT_METADATA[variant.code].label}</p>
                          <p className="text-xs text-slate-500">{variant.code}</p>
                        </div>
                        <span
                          className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${
                            variant.badge === 'W' ? 'bg-rose-100 text-rose-700' : 'bg-sky-100 text-sky-700'
                          }`}
                        >
                          {variant.badge}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </header>
  );
}

export default Header;
